// uses <audio/> element to play audio

type AudioMap = { t: number; w: number }[];

let audioElement;
let audioMap: AudioMap;
let audioFileID = "";

/** The audioElement `timeupdate` events are too infrequent, so using a higher frequency timer */
let updateTimerID;
let onPositionUpdate: (position: number) => void;
let setIsBuffering: (isBuffering: boolean) => void;

export const setupIfNecessary = (options: {
  audioFileID: string;
  onPositionUpdate: (position: number) => void;
  onBufferingChange: (isBuffering: boolean) => void;
  audioMap: AudioMap;
}) => {
  if (audioFileID === options.audioFileID) {
    // Already set up
    return;
  }

  audioMap = options.audioMap;
  audioFileID = options.audioFileID;
  onPositionUpdate = options.onPositionUpdate;
  setIsBuffering = options.onBufferingChange;

  audioElement = new Audio(`/api/audio/${audioFileID}`);
  document.body.appendChild(audioElement);
  audioElement.load();

  audioElement.addEventListener("waiting", () => {
    setIsBuffering(true);
  });
  audioElement.addEventListener("playing", () => {
    setIsBuffering(false);
  });
  audioElement.addEventListener("canplaythrough", () => {
    setIsBuffering(false);
  });

  // For debugging
  window["audioElement"] = audioElement;
};

export const updateAudioMap = (newAudioMap: AudioMap) => {
  audioMap = newAudioMap;
};

export const play = (position: number, rate: number) => {
  const time = getTimeFromPosition(position);
  audioElement.play();
  audioElement.currentTime = time;
  audioElement.playbackRate = rate;
  setTimeout(() => {
    audioElement.play();
    timerUpdate();
  }, 200);
};

export const jump = (seconds: number) => {
  audioElement.currentTime = Math.max(0, audioElement.currentTime + seconds);

  if (audioElement.paused) {
    const position = getPositionFromTime(audioElement.currentTime);
    onPositionUpdate(position);
  }
};

let lastPositionUpdate = -1;

const getPositionFromTime = (time: number) => {
  // use audioMap to find the last word index `w` for which t <= audioElement.currentTime
  // more efficient??
  let w = 0;
  for (let i = 0; i < audioMap.length; i++) {
    if (audioMap[i].t <= time) {
      w = audioMap[i].w;
    } else {
      break;
    }
  }

  return w;
};

const getTimeFromPosition = (position: number) => {
  for (let i = 0; i < audioMap.length; i++) {
    if (audioMap[i].w >= position) {
      return audioMap[i].t;
    }
  }
  return 0; // Default to 0 if position is not found
};

const timerUpdate = () => {
  const position = getPositionFromTime(audioElement.currentTime);

  if (position !== lastPositionUpdate) {
    onPositionUpdate(position);
    lastPositionUpdate = position;
  }

  if (audioElement.paused) {
    return;
  }

  updateTimerID = setTimeout(timerUpdate, 50);
};

export const pause = () => {
  audioElement.pause();
  clearTimeout(updateTimerID);
};
